import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Send, Paperclip, MoreVertical, ArrowLeft,
  CheckCheck, Check, Circle, FileText,
  Calendar, SlidersHorizontal,
  X, ExternalLink, Activity, Lock, AlertCircle, Forward, Search, History
} from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';
import { API, BACKEND_URL } from '@/config/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

const TIMELINE_MERGE_WINDOW_MS = 2 * 60 * 1000;

const parseTimelineTime = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};

const getTimelineEventTitle = (event) => {
  if (!event) return 'Activity';

  // Prefer document name when present (share/access/document-message)
  if (event.document_name) return event.document_name;

  // Fallbacks
  if (event.type === 'message') return event.message_type === 'document' ? 'Document message' : 'Message';
  if (event.type === 'share') return 'Document share';
  if (event.type === 'access_request') return 'Access request';
  return 'Activity';
};

const getTimelineEventStatus = (event) => {
  const type = (event?.event_type || '').toLowerCase();

  // Special combined event
  if (event?.type === 'share_bundle') {
    const isReceived = type.includes('received');
    return {
      dotClass: isReceived ? 'bg-blue-500' : 'bg-green-500',
      label: isReceived ? 'Received (with chat message)' : 'Shared (with chat message)'
    };
  }

  if (type.includes('shared')) return { dotClass: 'bg-green-500', label: 'Document shared' };
  if (type.includes('received') && event?.type === 'share') return { dotClass: 'bg-blue-500', label: 'Document received' };

  if (type.includes('message_sent')) return { dotClass: 'bg-green-500', label: 'Message sent' };
  if (type.includes('message_received')) return { dotClass: 'bg-blue-500', label: 'Message received' };

  if (type.includes('access_approved') || type.includes('access_granted')) return { dotClass: 'bg-green-500', label: 'Access approved' };
  if (type.includes('access_rejected') || type.includes('access_denied')) return { dotClass: 'bg-red-500', label: 'Access rejected' };
  if (type.includes('access_requested') || type.includes('access_received')) return { dotClass: 'bg-amber-500', label: 'Access requested' };

  // Fallback
  if (event?.status === 'revoked' || event?.status === 'rejected') return { dotClass: 'bg-red-500', label: 'Action blocked' };
  return { dotClass: 'bg-gray-400', label: 'Activity' };
};

/**
 * Combine "document_shared" + "message_sent(document)" that happen together.
 * Backend creates both when using /share/direct (it also posts a document message in chat).
 */
const combineTimelineEvents = (events = []) => {
  const sorted = [...events].sort((a, b) => {
    const ta = parseTimelineTime(a?.timestamp) ?? 0;
    const tb = parseTimelineTime(b?.timestamp) ?? 0;
    return ta - tb; // oldest -> newest
  });

  const combined = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const cur = sorted[i];
    const next = sorted[i + 1];

    const curTime = parseTimelineTime(cur?.timestamp);
    const nextTime = parseTimelineTime(next?.timestamp);

    const isShare = cur?.type === 'share';
    const isNextDocMessage = next?.type === 'message' && next?.message_type === 'document';

    const sameDoc = !!cur?.udt_id && cur?.udt_id === next?.udt_id;
    const sameSender = !!cur?.sender_id && cur?.sender_id === next?.sender_id;
    const withinWindow =
      curTime != null && nextTime != null && nextTime >= curTime && (nextTime - curTime) <= TIMELINE_MERGE_WINDOW_MS;

    if (isShare && isNextDocMessage && sameDoc && sameSender && withinWindow) {
      combined.push({
        type: 'share_bundle',
        event_type: cur?.event_type || 'document_shared',
        udt_id: cur?.udt_id,
        timestamp: cur?.timestamp,
        sender_id: cur?.sender_id,
        receiver_id: cur?.receiver_id,
        sender_name: cur?.sender_name,
        receiver_name: cur?.receiver_name,
        document_name: cur?.document_name,
        document_type: cur?.document_type,
        purpose: cur?.purpose,
        status: cur?.status,
        expiry: cur?.expiry,
        share_id: cur?.share_id,
        message_id: next?.message_id,
        share_message: cur?.message || '',
        chat_text: next?.content?.text || ''
      });
      i += 1; // skip the next event too
      continue;
    }

    combined.push(cur);
  }

  return combined;
};

const WS_URL = (() => {
  if (BACKEND_URL) {
    // http(s)://... -> ws(s)://...
    return BACKEND_URL.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
  }
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
})();

const conversationFilterDefaults = {
  search: '',
  dateFrom: '',
  dateTo: '',
  quickRange: 'all'
};

const historyFilterDefaults = {
  search: '',
  purpose: '',
  recipient: '',
  documentTypes: [],
  statuses: [],
  dateFrom: '',
  dateTo: '',
  quickRange: 'all'
};

const QUICK_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' }
];

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const filterConversations = (items = [], filters = conversationFilterDefaults) => {
  const search = (filters.search || '').trim().toLowerCase();
  const fromTime = filters.dateFrom ? new Date(filters.dateFrom).getTime() : null;
  const toTime = filters.dateTo ? new Date(filters.dateTo).getTime() + 86399999 : null;

  return items.filter((conversation) => {
    const lastMessageTime = conversation?.last_message_at
      ? new Date(conversation.last_message_at).getTime()
      : null;

    if (search) {
      const haystack = [
        conversation?.other_user?.name,
        conversation?.other_user?.email,
        conversation?.other_user?.udt_user_id,
        conversation?.last_message
      ]
        .map((value) => (value || '').toLowerCase());

      if (!haystack.some((value) => value.includes(search))) {
        return false;
      }
    }

    if ((fromTime || toTime) && !lastMessageTime) {
      return false;
    }

    if (fromTime && lastMessageTime < fromTime) {
      return false;
    }

    if (toTime && lastMessageTime > toTime) {
      return false;
    }

    return true;
  });
};

const filterShareHistory = (shares = [], filters = historyFilterDefaults) => {
  const search = (filters.search || '').trim().toLowerCase();
  const purposeQuery = (filters.purpose || '').trim().toLowerCase();
  const recipientQuery = (filters.recipient || '').trim().toLowerCase();
  const documentTypes = filters.documentTypes || [];
  const statuses = filters.statuses || [];
  const fromTime = filters.dateFrom ? new Date(filters.dateFrom).getTime() : null;
  const toTime = filters.dateTo ? new Date(filters.dateTo).getTime() + 86399999 : null;

  return shares.filter((share) => {
    const createdTime = share?.created_at ? new Date(share.created_at).getTime() : null;

    if (search) {
      const haystack = [
        share?.recipient?.name,
        share?.recipient?.email,
        share?.udt_id,
        share?.document?.file_name
      ]
        .map((value) => (value || '').toLowerCase());

      if (!haystack.some((value) => value.includes(search))) {
        return false;
      }
    }

    if (recipientQuery) {
      const recipientName = (share?.recipient?.name || '').toLowerCase();
      const recipientEmail = (share?.recipient?.email || '').toLowerCase();
      if (!recipientName.includes(recipientQuery) && !recipientEmail.includes(recipientQuery)) {
        return false;
      }
    }

    if (documentTypes.length) {
      const type = share?.document?.file_type || 'Unknown';
      if (!documentTypes.includes(type)) {
        return false;
      }
    }

    if (statuses.length) {
      const status = share?.status || 'unknown';
      if (!statuses.includes(status)) {
        return false;
      }
    }

    if (purposeQuery) {
      const docPurpose = (share?.document?.purpose || '').toLowerCase();
      const messagePurpose = (share?.message || '').toLowerCase();
      if (!docPurpose.includes(purposeQuery) && !messagePurpose.includes(purposeQuery)) {
        return false;
      }
    }

    if ((fromTime || toTime) && !createdTime) {
      return false;
    }

    if (fromTime && createdTime < fromTime) {
      return false;
    }

    if (toTime && createdTime > toTime) {
      return false;
    }

    return true;
  });
};

const formatShareDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return value;
  }
};

export default function MessagingPage({ token, user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [ws, setWs] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [userDocuments, setUserDocuments] = useState([]);
  const [conversationFilters, setConversationFilters] = useState(() => ({ ...conversationFilterDefaults }));
  const [conversationSearchValue, setConversationSearchValue] = useState(conversationFilterDefaults.search);
  const [showConversationFilters, setShowConversationFilters] = useState(false);
  const [shareHistory, setShareHistory] = useState([]);
  const [filteredShareHistory, setFilteredShareHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilters, setHistoryFilters] = useState(() => ({ ...historyFilterDefaults }));
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [showHistoryFiltersPanel, setShowHistoryFiltersPanel] = useState(true);
  const [timelineData, setTimelineData] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [documentStatuses, setDocumentStatuses] = useState({}); // Track document lock status
  const [clearChatDialogOpen, setClearChatDialogOpen] = useState(false);
  // Reshare from chat (when recipient has allow_reshare and doc not locked)
  const [showReshareDialog, setShowReshareDialog] = useState(false);
  const [reshareDoc, setReshareDoc] = useState(null);
  const [reshareRecipient, setReshareRecipient] = useState(null);
  const [reshareMessage, setReshareMessage] = useState('');
  const [reshareExpiryHours, setReshareExpiryHours] = useState(24);
  const [resharePermissions, setResharePermissions] = useState('view_only');
  const [reshareSearchQuery, setReshareSearchQuery] = useState('');
  const [reshareSearchResults, setReshareSearchResults] = useState([]);
  const [reshareLoading, setReshareLoading] = useState(false);
  const [documentHistoryOpen, setDocumentHistoryOpen] = useState(false);
  const [documentHistoryUdtId, setDocumentHistoryUdtId] = useState('');
  const [documentHistoryData, setDocumentHistoryData] = useState(null);
  const [documentHistoryLoading, setDocumentHistoryLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sharingDocId, setSharingDocId] = useState(null);
  const [clearingChat, setClearingChat] = useState(false);
  
  const messagesEndRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const activeConversationRef = useRef(null);

  const combinedTimeline = useMemo(
    () => combineTimelineEvents(timelineData?.timeline || []),
    [timelineData]
  );

  const filteredConversations = useMemo(
    () => filterConversations(conversations, conversationFilters),
    [conversations, conversationFilters]
  );

  const conversationActiveFilterCount = useMemo(() => {
    let count = 0;
    if (conversationFilters.search) count += 1;
    if (conversationFilters.dateFrom) count += 1;
    if (conversationFilters.dateTo) count += 1;
    return count;
  }, [conversationFilters]);

  const historyActiveFilterCount = useMemo(() => {
    let count = 0;
    if (historyFilters.search) count += 1;
    if (historyFilters.purpose) count += 1;
    if (historyFilters.recipient) count += 1;
    count += historyFilters.documentTypes.length;
    count += historyFilters.statuses.length;
    if (historyFilters.dateFrom) count += 1;
    if (historyFilters.dateTo) count += 1;
    return count;
  }, [historyFilters]);

  const fetchTimeline = async (udtUserId) => {
    try {
      setTimelineLoading(true);
      const response = await axios.get(`${API}/activity/timeline/${udtUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimelineData(response.data);
      setShowTimeline(true);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      toast.error('Failed to load activity timeline');
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleUDTIdClick = (udtUserId) => {
    if (udtUserId && udtUserId.startsWith('UDT-USER-')) {
      fetchTimeline(udtUserId);
    } else {
      toast.error('Invalid UDT User ID');
    }
  };

  const fetchDocumentHistory = async (udtId) => {
    if (!udtId) return;
    try {
      setDocumentHistoryLoading(true);
      setDocumentHistoryUdtId(udtId);
      const response = await axios.get(`${API}/documents/${udtId}/document-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentHistoryData(response.data);
      setDocumentHistoryOpen(true);
    } catch (error) {
      console.error('Failed to fetch document history:', error);
      toast.error(error.response?.data?.detail || 'Failed to load document history');
    } finally {
      setDocumentHistoryLoading(false);
    }
  };

  const openDocumentHistory = (udtId) => {
    if (!udtId) return;
    setDocumentHistoryData(null);
    fetchDocumentHistory(udtId);
  };

  useEffect(() => {
    fetchConversations();
    fetchUserDocuments();
    fetchShareHistory();
    connectWebSocket();

    return () => {
      if (ws) ws.close();
    };
  }, []);

  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  // Poll for new messages when a conversation is open (fallback if WebSocket misses)
  useEffect(() => {
    if (!activeConversation?.conversation_id) return;
    const interval = setInterval(() => {
      fetchMessages(activeConversation.conversation_id);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeConversation?.conversation_id]);

  // Periodically refresh document statuses for active conversation
  useEffect(() => {
    if (!activeConversation || !messages.length) return;

    const refreshDocumentStatuses = async () => {
      const statusChecks = {};
      for (const msg of messages) {
        if (msg.type === 'document' && msg.content?.document) {
          const doc = msg.content.document;
          const viewerLink = doc.viewer_link || '';
          const match = viewerLink.match(/\/viewer\/([^?]+)\?token=([^&]+)/);
          if (match) {
            const [, udtId, shareToken] = match;
            const status = await checkDocumentStatus(udtId, shareToken);
            statusChecks[`${udtId}_${shareToken}`] = status;
          }
        }
      }
      if (Object.keys(statusChecks).length > 0) {
        setDocumentStatuses(prev => ({ ...prev, ...statusChecks }));
      }
    };

    // Refresh immediately when conversation or messages change
    refreshDocumentStatuses();

    // Refresh every 15 seconds to catch real-time status changes (after misuse detection)
    const interval = setInterval(refreshDocumentStatuses, 15000);

    return () => clearInterval(interval);
  }, [activeConversation?.conversation_id, messages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setConversationSearchValue(conversationFilters.search || '');
  }, [conversationFilters.search]);

  useEffect(() => {
    setFilteredShareHistory(filterShareHistory(shareHistory, historyFilters));
  }, [shareHistory, historyFilters]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const connectWebSocket = () => {
    if (!user?.user_id) {
      console.error('Cannot connect WebSocket: user_id not available');
      return;
    }

    try {
      const websocket = new WebSocket(`${WS_URL}/ws/chat/${user.user_id}`);

    websocket.onopen = () => {
      console.log('WebSocket connected');
    };

    websocket.onmessage = async (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'new_message') {
        const current = activeConversationRef.current;
        if (current && data.message.conversation_id === current.conversation_id) {
          setMessages(prev => {
            const id = data.message.message_id;
            if (id && prev.some(m => m.message_id === id)) return prev;
            return [...prev, data.message];
          });
          
          // Check document status if it's a document message
          if (data.message.type === 'document' && data.message.content?.document) {
            const doc = data.message.content.document;
            const viewerLink = doc.viewer_link || '';
            const match = viewerLink.match(/\/viewer\/([^?]+)\?token=([^&]+)/);
            if (match) {
              const [, udtId, shareToken] = match;
              const status = await checkDocumentStatus(udtId, shareToken);
              setDocumentStatuses(prev => ({
                ...prev,
                [`${udtId}_${shareToken}`]: status
              }));
            }
          }
        }
        fetchConversations(); // Update conversation list
      } else if (data.type === 'typing') {
        // Handle typing indicator
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast.error('Real-time messaging connection error');
    };

    websocket.onclose = () => {
      console.log('WebSocket disconnected');
      // Reconnect after 3 seconds
      setTimeout(connectWebSocket, 3000);
    };

    setWs(websocket);
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      toast.error('Failed to connect to messaging service');
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const convs = response.data.conversations || [];
      setConversations(convs);
      
      // Auto-select conversation if owner_id is provided in URL
      const openWithOwnerId = searchParams.get('openConversationWith');
      if (openWithOwnerId && convs.length > 0) {
        const targetConv = convs.find(conv => {
          const otherUser = conv.other_user;
          return otherUser && (otherUser.user_id === openWithOwnerId || otherUser.udt_user_id === openWithOwnerId);
        });
        if (targetConv) {
          selectConversation(targetConv);
          // Clear URL param after selection
          setSearchParams({});
        } else {
          // Conversation doesn't exist yet - create it
          try {
            const createResp = await axios.post(
              `${API}/chat/conversations/create`,
              { recipient_id: openWithOwnerId },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            // Refresh conversations and select the new one
            const refreshResp = await axios.get(`${API}/chat/conversations`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const refreshedConvs = refreshResp.data.conversations || [];
            setConversations(refreshedConvs);
            const newConv = refreshedConvs.find(c => c.conversation_id === createResp.data.conversation_id);
            if (newConv) {
              selectConversation(newConv);
            }
            setSearchParams({});
          } catch (createError) {
            console.error('Failed to create conversation:', createError);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      setConversations([]);
      toast.error('Failed to load conversations');
    }
  };

  const fetchUserDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDocuments(response.data.documents?.filter(doc => doc.status === 'active') || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setUserDocuments([]);
    }
  };

  const handleConversationFilterChange = (key, value) => {
    setConversationFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'dateFrom' || key === 'dateTo') {
        next.quickRange = value ? 'custom' : (key === 'dateFrom'
          ? (next.dateTo ? 'custom' : 'all')
          : (next.dateFrom ? 'custom' : 'all'));
        if (!next.dateFrom && !next.dateTo) {
          next.quickRange = 'all';
        }
      }
      return next;
    });
  };

  const clearConversationFilters = () => {
    setConversationFilters({ ...conversationFilterDefaults });
  };

  const handleConversationQuickRangeChange = (value) => {
    if (value === 'all') {
      setConversationFilters((prev) => ({
        ...prev,
        quickRange: value,
        dateFrom: '',
        dateTo: ''
      }));
      return;
    }

    const now = new Date();
    const quickRangeMap = {
      '24h': 1,
      '7d': 7,
      '30d': 30
    };
    const days = quickRangeMap[value] || 0;
    const fromDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    setConversationFilters((prev) => ({
      ...prev,
      quickRange: value,
      dateFrom: toDateInputValue(fromDate),
      dateTo: toDateInputValue(now)
    }));
  };

  const handleConversationSearchInput = (value) => {
    setConversationSearchValue(value);
    setConversationFilters((prev) => ({ ...prev, search: value }));
  };

  const fetchShareHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await axios.get(`${API}/share/direct/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShareHistory(response.data.shares || []);
    } catch (error) {
      console.error('Failed to fetch share history:', error);
      toast.error('Failed to load share history');
      setShareHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryFilterChange = (key, value) => {
    setHistoryFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'dateFrom' || key === 'dateTo') {
        next.quickRange = value ? 'custom' : (key === 'dateFrom'
          ? (next.dateTo ? 'custom' : 'all')
          : (next.dateFrom ? 'custom' : 'all'));
        if (!next.dateFrom && !next.dateTo) {
          next.quickRange = 'all';
        }
      }
      return next;
    });
  };

  const toggleHistoryArrayFilter = (key, value) => {
    setHistoryFilters((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(value);
      const nextValues = exists
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [key]: nextValues };
    });
  };

  const clearHistoryFilters = () => {
    setHistoryFilters({ ...historyFilterDefaults });
  };

  const handleHistoryQuickRangeChange = (value) => {
    if (value === 'all') {
      setHistoryFilters((prev) => ({
        ...prev,
        quickRange: value,
        dateFrom: '',
        dateTo: ''
      }));
      return;
    }

    const now = new Date();
    const quickRangeMap = {
      '24h': 1,
      '7d': 7,
      '30d': 30
    };
    const days = quickRangeMap[value] || 0;
    const fromDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    setHistoryFilters((prev) => ({
      ...prev,
      quickRange: value,
      dateFrom: toDateInputValue(fromDate),
      dateTo: toDateInputValue(now)
    }));
  };

  const renderConversationFilters = () => (
    <div className="p-4 space-y-4 bg-gray-50 border-b">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4" />
          Filter Conversations
        </h3>
        {conversationActiveFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearConversationFilters}
            className="h-7 text-xs text-red-600 hover:text-red-700"
          >
            Clear All
          </Button>
        )}
      </div>
      <div className="grid gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase text-gray-500 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Time range
          </Label>
          <div className="flex flex-wrap gap-2">
            {QUICK_RANGE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                variant={conversationFilters.quickRange === option.value ? 'default' : 'outline'}
                onClick={() => handleConversationQuickRangeChange(option.value)}
                className={conversationFilters.quickRange === option.value ? 'bg-blue-600 text-white' : 'bg-white'}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-500">From</Label>
              <Input
                type="date"
                value={conversationFilters.dateFrom}
                onChange={(e) => handleConversationFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">To</Label>
              <Input
                type="date"
                value={conversationFilters.dateTo}
                onChange={(e) => handleConversationFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="pt-2">
          {conversationActiveFilterCount > 0 && (
            <div className="flex flex-wrap gap-2 text-xs">
              {conversationFilters.search && (
                <Badge variant="outline" className="gap-1">
                  Search: {conversationFilters.search}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => handleConversationFilterChange('search', '')} />
                </Badge>
              )}
              {(conversationFilters.dateFrom || conversationFilters.dateTo) && (
                <Badge variant="outline" className="gap-1">
                  Date: {conversationFilters.dateFrom || '…'} → {conversationFilters.dateTo || '…'}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => {
                      handleConversationFilterChange('dateFrom', '');
                      handleConversationFilterChange('dateTo', '');
                    }}
                  />
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderHistoryFilters = () => (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Input
          placeholder="Search by recipient, email, document or UDT ID..."
          value={historyFilters.search}
          onChange={(e) => handleHistoryFilterChange('search', e.target.value)}
        />
        <Input
          placeholder="Purpose or message context..."
          value={historyFilters.purpose}
          onChange={(e) => handleHistoryFilterChange('purpose', e.target.value)}
        />
      </div>

      <div>
        <Input
          placeholder="Recipient name or email..."
          value={historyFilters.recipient}
          onChange={(e) => handleHistoryFilterChange('recipient', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase text-gray-500 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Time range
        </Label>
        <div className="flex flex-wrap gap-2">
          {QUICK_RANGE_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="sm"
              variant={historyFilters.quickRange === option.value ? 'default' : 'outline'}
              onClick={() => handleHistoryQuickRangeChange(option.value)}
              className={historyFilters.quickRange === option.value ? 'bg-blue-600 text-white' : 'bg-white'}
            >
              {option.label}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-gray-500">From</Label>
            <Input
              type="date"
              value={historyFilters.dateFrom}
              onChange={(e) => handleHistoryFilterChange('dateFrom', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">To</Label>
            <Input
              type="date"
              value={historyFilters.dateTo}
              onChange={(e) => handleHistoryFilterChange('dateTo', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label className="text-xs uppercase text-gray-500">Document type</Label>
          <div className="mt-2 rounded-md border bg-white max-h-36 overflow-y-auto divide-y">
            {Array.from(new Set(shareHistory.map((share) => share?.document?.file_type || 'Unknown'))).map((type) => (
              <label
                key={type}
                className="flex items-center gap-2 text-sm px-3 py-2 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={historyFilters.documentTypes.includes(type)}
                  onChange={() => toggleHistoryArrayFilter('documentTypes', type)}
                />
                <span>{type}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase text-gray-500">Status</Label>
          <div className="mt-2 rounded-md border bg-white max-h-36 overflow-y-auto divide-y">
            {['active', 'revoked', 'expired'].map((status) => (
              <label
                key={status}
                className="flex items-center gap-2 text-sm px-3 py-2 cursor-pointer hover:bg-gray-50 capitalize"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={historyFilters.statuses.includes(status)}
                  onChange={() => toggleHistoryArrayFilter('statuses', status)}
                />
                <span>{status}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {historyActiveFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {historyFilters.search && (
            <Badge variant="outline" className="gap-1">
              Search: {historyFilters.search}
              <X className="w-3 h-3 cursor-pointer" onClick={() => handleHistoryFilterChange('search', '')} />
            </Badge>
          )}
          {historyFilters.purpose && (
            <Badge variant="outline" className="gap-1">
              Purpose: {historyFilters.purpose}
              <X className="w-3 h-3 cursor-pointer" onClick={() => handleHistoryFilterChange('purpose', '')} />
            </Badge>
          )}
          {historyFilters.recipient && (
            <Badge variant="outline" className="gap-1">
              Recipient: {historyFilters.recipient}
              <X className="w-3 h-3 cursor-pointer" onClick={() => handleHistoryFilterChange('recipient', '')} />
            </Badge>
          )}
          {historyFilters.documentTypes.map((type) => (
            <Badge key={type} variant="outline" className="gap-1">
              Type: {type}
              <X className="w-3 h-3 cursor-pointer" onClick={() => toggleHistoryArrayFilter('documentTypes', type)} />
            </Badge>
          ))}
          {historyFilters.statuses.map((status) => (
            <Badge key={status} variant="outline" className="gap-1 capitalize">
              Status: {status}
              <X className="w-3 h-3 cursor-pointer" onClick={() => toggleHistoryArrayFilter('statuses', status)} />
            </Badge>
          ))}
          {(historyFilters.dateFrom || historyFilters.dateTo) && (
            <Badge variant="outline" className="gap-1">
              Date: {historyFilters.dateFrom || '…'} → {historyFilters.dateTo || '…'}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => {
                  handleHistoryFilterChange('dateFrom', '');
                  handleHistoryFilterChange('dateTo', '');
                }}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );

  const checkDocumentStatus = async (udtId, shareToken) => {
    if (!udtId || !shareToken) return 'revoked'; // Assume revoked if missing params
    try {
      const response = await axios.get(`${API}/access/token-info`, {
        params: { udt_id: udtId, token: shareToken },
      });
      const tokenStatus = response.data?.status;

      // Only the viewer's token status determines access.
      if (tokenStatus === 'active') {
        return 'active';
      }

      // Any other status (revoked, invalid, null, undefined) means access is revoked.
      return 'revoked';
    } catch (error) {
      // If API call fails, assume revoked for security
      console.warn('Failed to check document status:', error);
      return 'revoked';
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const messagesData = response.data.messages || [];
      setMessages(messagesData);

      // Check document statuses for all document messages
      const statusChecks = {};
      for (const msg of messagesData) {
        if (msg.type === 'document' && msg.content.document) {
          const doc = msg.content.document;
          const viewerLink = doc.viewer_link || '';
          // Extract token from viewer link (format: /viewer/{udtId}?token={token})
          const match = viewerLink.match(/\/viewer\/([^?]+)\?token=([^&]+)/);
          if (match) {
            const [, udtId, shareToken] = match;
            const status = await checkDocumentStatus(udtId, shareToken);
            statusChecks[`${udtId}_${shareToken}`] = status;
          }
        }
      }
      setDocumentStatuses(statusChecks);

      // Mark as read
      await axios.post(`${API}/chat/messages/read/${conversationId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setMessages([]);
      toast.error('Failed to load messages');
    }
  };

  const selectConversation = (conv) => {
    setActiveConversation(conv);
    fetchMessages(conv.conversation_id);
  };

  const clearChat = async () => {
    if (!activeConversation?.conversation_id) return;

    try {
      setClearingChat(true);
      await axios.post(
        `${API}/chat/conversations/clear/${activeConversation.conversation_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages([]);
      toast.success('Chat cleared');
      setClearChatDialogOpen(false);
      fetchConversations();
    } catch (error) {
      console.error('Failed to clear chat:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear chat');
    } finally {
      setClearingChat(false);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !activeConversation || sendingMessage) return;

    try {
      setSendingMessage(true);
      await axios.post(
        `${API}/chat/messages/send`,
        {
          conversation_id: activeConversation.conversation_id,
          message_type: 'text',
          content: { text: messageText }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessageText('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const shareDocument = async (doc) => {
    if (!activeConversation) return;

    try {
      setSharingDocId(doc.udt_id);
      const recipientId = activeConversation.other_user.user_id;

      await axios.post(
        `${API}/share/direct`,
        {
          udt_id: doc.udt_id,
          recipient_id: recipientId,
          message: `📎 Shared: ${doc.file_name}`,
          expiry_hours: 24
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Document shared successfully!');
      setShowDocumentDialog(false);
      if (activeConversation?.conversation_id) {
        fetchMessages(activeConversation.conversation_id);
      }
    } catch (error) {
      toast.error('Failed to share document');
    } finally {
      setSharingDocId(null);
    }
  };

  const openReshareDialog = (doc) => {
    setReshareDoc(doc);
    setReshareRecipient(null);
    setReshareMessage(doc?.file_name ? `Forwarded: ${doc.file_name}` : '');
    setReshareSearchQuery('');
    setReshareSearchResults([]);
    setReshareExpiryHours(24);
    setResharePermissions('view_only');
    setShowReshareDialog(true);
  };

  const searchUsersForReshare = async (query) => {
    if (!query.trim()) {
      setReshareSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/chat/users/search?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = response.data?.users || [];
      setReshareSearchResults(list);
    } catch (err) {
      setReshareSearchResults([]);
    }
  };

  const submitReshare = async () => {
    if (!reshareDoc?.udt_id || !reshareRecipient?.user_id) {
      toast.error('Please select a recipient');
      return;
    }
    try {
      setReshareLoading(true);
      await axios.post(
        `${API}/share/reshare`,
        {
          udt_id: reshareDoc.udt_id,
          recipient_id: reshareRecipient.user_id,
          message: reshareMessage || `Forwarded: ${reshareDoc.file_name || reshareDoc.udt_id}`,
          expiry_hours: reshareExpiryHours,
          permissions: resharePermissions || 'view_only'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document forwarded successfully');
      setShowReshareDialog(false);
      setReshareDoc(null);
      setReshareRecipient(null);
      if (activeConversation?.conversation_id) fetchMessages(activeConversation.conversation_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reshare failed');
    } finally {
      setReshareLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(`${API}/chat/users/search?query=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.users);
    } catch (error) {
      console.error('Search failed');
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(query);
    }, 300);
  };

  const startConversation = async (userId) => {
    try {
      const response = await axios.post(
        `${API}/chat/conversations/create`,
        { recipient_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowSearch(false);
      setSearchQuery('');
      setSearchResults([]);
      fetchConversations();

      // Select the conversation
      setTimeout(() => {
        const conv = conversations.find(c => c.conversation_id === response.data.conversation_id);
        if (conv) selectConversation(conv);
      }, 500);
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  return (
    <DashboardLayout user={user} token={token} onLogout={onLogout}>
      <div className="flex flex-col lg:flex-row h-[calc(100dvh-80px)] sm:h-[calc(100vh-80px)] lg:h-[calc(100vh-0px)] bg-gray-50 overflow-hidden min-w-0">
        {/* Left Sidebar - Conversations & Filters */}
        <div className={`
          ${activeConversation ? 'hidden lg:flex' : 'flex'}
          w-full lg:w-96 bg-white border-r flex-col min-w-0
        `}>
          <div className="flex-1 flex flex-col min-w-0">
            <div className="p-3 sm:p-4 border-b space-y-3 sm:space-y-4">
              <div className="flex flex-col gap-3">
                <div className="space-y-2">
                  <h2 className="text-lg sm:text-xl font-bold" style={{ color: '#0F52BA' }}>Messages</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      placeholder="Search conversations..."
                      value={conversationSearchValue}
                      onChange={(e) => handleConversationSearchInput(e.target.value)}
                      className="flex-1 min-w-0 w-full sm:min-w-[180px]"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowConversationFilters((prev) => !prev)}
                      className="flex items-center gap-2"
                      style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      Filters
                      {conversationActiveFilterCount > 0 && (
                        <Badge className="ml-1" style={{ background: '#0F52BA', color: '#fff' }}>
                          {conversationActiveFilterCount}
                        </Badge>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setHistoryPanelOpen(true)}
                      className="flex items-center gap-2"
                      style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                    >
                      History
                      {historyActiveFilterCount > 0 && (
                        <Badge className="ml-1" style={{ background: '#0F52BA', color: '#fff' }}>
                          {historyActiveFilterCount}
                        </Badge>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowSearch(!showSearch)}
                      style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                    >
                      New Chat
                    </Button>
                  </div>
                </div>

                {showSearch && (
                  <div className="space-y-2">
                    <Input
                      placeholder="Start a new chat by searching name, email, mobile number, or UDT User ID..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    {searchResults.length > 0 && (
                      <div className="bg-gray-50 rounded-md max-h-60 overflow-y-auto">
                        {searchResults.map(user => (
                          <div
                            key={user.user_id}
                            onClick={() => startConversation(user.user_id)}
                            className="p-3 hover:bg-gray-100 cursor-pointer border-b"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">{user.name}</p>
                                <p className="text-sm text-gray-600">{user.email}</p>
                              </div>
                              {user.udt_user_id && (
                                <div className="text-xs font-mono bg-blue-100 px-2 py-1 rounded" style={{ color: '#0F52BA' }}>
                                  {user.udt_user_id}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {showConversationFilters && renderConversationFilters()}
              </div>
            </div>
            <ScrollArea className="flex-1">
              {filteredConversations.map(conv => (
                <div
                  key={conv.conversation_id}
                  onClick={() => selectConversation(conv)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                    activeConversation?.conversation_id === conv.conversation_id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{conv.other_user.name}</p>
                        {conv.other_user.online && (
                          <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                        )}
                        {conv.other_user.udt_user_id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUDTIdClick(conv.other_user.udt_user_id);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            title="View complete timeline with this user"
                          >
                            Timeline: {conv.other_user.udt_user_id}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {conv.last_message || 'Start conversation...'}
                      </p>
                    </div>
                    {conv.last_message_at && (
                      <p className="text-xs text-gray-400">
                        {new Date(conv.last_message_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {filteredConversations.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No conversations match the current filters.</p>
                  {!showSearch && (
                    <p className="text-sm">Use New Chat to start a conversation.</p>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* Right Panel - Chat */}
        {activeConversation ? (
          <div className={`
            ${activeConversation ? 'flex' : 'hidden lg:flex'}
            flex-1 flex-col bg-gray-50
          `}>
            {/* Chat Header */}
            <div className="bg-white p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Back button for mobile */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveConversation(null)}
                  className="lg:hidden"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                  {activeConversation.other_user.name[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold">{activeConversation.other_user.name}</p>
                  <p className="text-xs text-gray-500">
                    {activeConversation.other_user.online ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[180px]">
                    <DropdownMenuItem onClick={() => setHistoryPanelOpen(true)}>
                      Share history
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const udt = activeConversation?.other_user?.udt_user_id;
                        if (udt) fetchTimeline(udt);
                      }}
                    >
                      Activity timeline
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => setClearChatDialogOpen(true)}
                    >
                      Clear chat
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4 bg-gray-100">
              {messages.map(msg => {
                const isMine = msg.sender_id === user.user_id;

                return (
                  <div
                    key={msg.message_id}
                    className={`flex mb-3 ${isMine ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-md px-4 py-2 rounded-lg ${
                        isMine
                          ? 'bg-green-500 text-white'
                          : 'bg-white text-gray-800 shadow-sm'
                      }`}
                    >
                      {msg.type === 'text' && (
                        <p className="whitespace-pre-wrap break-words">{msg.content.text}</p>
                      )}

                      {msg.type === 'document' && (() => {
                        const doc = msg?.content?.document;
                        if (!doc) {
                          return (
                            <div className="text-xs text-red-600">
                              Document metadata is unavailable for this message.
                            </div>
                          );
                        }
                        const viewerLink = doc?.viewer_link || '';
                        const match = viewerLink.match(/\/viewer\/([^?]+)\?token=([^&]+)/);
                        const docKey = match ? `${match[1]}_${match[2]}` : null;
                        // Check if locked: status is 'revoked' OR status is not 'active' (including null/undefined)
                        const status = docKey ? documentStatuses[docKey] : null;
                        const isLocked = docKey && (status === 'revoked' || status !== 'active');
                        const isOwnerMessage = isMine && doc?.udt_id;
                        
                        return (
                          <div className="space-y-2">
                            {/* Document Card - Green bubble style when locked, normal when active */}
                            <div 
                              className={`p-3 rounded-lg ${isLocked ? 'bg-green-500 cursor-not-allowed' : 'bg-white/20'}`}
                              onClick={(e) => {
                                // Prevent navigation if locked
                                if (isLocked) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  return;
                                }
                              }}
                              onMouseDown={(e) => {
                                // Prevent any interaction when locked
                                if (isLocked) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }
                              }}
                            >
                              {/* Document Name */}
                              <p className={`font-semibold text-sm mb-2 break-all ${isLocked ? 'text-white' : 'text-gray-800'}`}>
                                {doc.file_name}
                              </p>
                              
                              {/* Lock Status Message */}
                              {isLocked ? (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-white text-xs mb-2">
                                    <Lock className="w-4 h-4" />
                                    <span>Document locked due to misuse. Cannot open until owner approves.</span>
                                  </div>
                                  
                                  {/* Red Warning Box Inside Green Bubble */}
                                  <div className="bg-red-50 border-2 border-red-300 rounded p-3 mt-2">
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                      <div className="flex-1">
                                        <p className="text-xs font-semibold text-red-800 mb-1">
                                          Security Violation Detected
                                        </p>
                                        <p className="text-xs text-red-700">
                                          This document has been locked due to security violation. Please contact the document owner for approval to access.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* UDT ID */}
                                  {doc.udt_id && (
                                    <div className="mt-2 text-xs text-white/80">
                                      UDT: {doc.udt_id}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="p-0 h-auto text-xs group relative"
                                      disabled={isLocked}
                                      onClick={async (e) => {
                                        // Double-check lock status before navigation
                                        if (isLocked) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          return;
                                        }
                                        e.preventDefault();
                                        e.stopPropagation();
                                        
                                        if (isOwnerMessage) {
                                          navigate(`/dashboard/documents/view/${doc.udt_id}`);
                                        } else {
                                          // Additional safety: check status before navigating
                                          if (match && match[1] && match[2]) {
                                            const currentStatus = await checkDocumentStatus(match[1], match[2]);
                                            if (currentStatus !== 'active') {
                                              // Update status and prevent navigation
                                              const key = `${match[1]}_${match[2]}`;
                                              setDocumentStatuses(prev => ({
                                                ...prev,
                                                [key]: 'revoked'
                                              }));
                                              return; // Don't navigate - document is locked
                                            }
                                          }
                                          navigate(doc.viewer_link);
                                        }
                                      }}
                                    >
                                      {isOwnerMessage ? 'Open (Owner) →' : 'View Document →'}
                                    </Button>
                                    {/* Reshare (forward) icon: only when recipient has allow_reshare and doc not locked */}
                                    {!isMine && doc.allow_reshare && !isLocked && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="p-1 h-8 w-8 rounded-full text-gray-600 hover:bg-gray-200"
                                        title="Forward / Reshare"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          openReshareDialog(doc);
                                        }}
                                      >
                                        <Forward className="w-4 h-4" />
                                      </Button>
                                    )}
                                    {doc.udt_id && (
                                      <span className="text-xs opacity-70">
                                        UDT: {doc.udt_id}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* View History - only for recipient, in place of timeline (timeline is for entire chat, not per message) */}
                            {msg.type === 'document' && !isMine && doc?.udt_id && (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDocumentHistory(doc.udt_id); }}
                                disabled={documentHistoryLoading}
                                className="mt-2 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-800 hover:bg-blue-100"
                                title="See where this document came from"
                              >
                                <History className="w-4 h-4" />
                                View History
                              </button>
                            )}
                            
                            {/* Message Text */}
                            {msg.content.text && (
                              <p className={`text-sm break-words ${isMine ? 'text-white' : 'text-gray-800'}`}>
                                {msg.content.text}
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      <div className="flex items-center justify-end gap-1 mt-1">
                        <span className="text-xs opacity-70">
                          {new Date(msg.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {isMine && (
                          <span className="text-xs">
                            {msg.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </ScrollArea>

            {/* Message Input */}
            <div className="bg-white p-4 border-t">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDocumentDialog(true)}
                >
                  <Paperclip className="w-5 h-5" />
                </Button>
                <Input
                  placeholder="Type a message..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  className="flex-1"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!messageText.trim() || sendingMessage}
                  style={{ background: sendingMessage ? '#9ca3af' : '#0F52BA' }}
                  className="flex items-center justify-center gap-2"
                >
                  {sendingMessage ? (
                    <>
                      <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending…</span>
                    </>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden lg:flex flex-1 items-center justify-center bg-gray-50">
            <div className="text-center text-gray-500">
              <p className="text-xl mb-2">Select a conversation</p>
              <p className="text-sm">Choose a conversation to start messaging</p>
            </div>
          </div>
        )}

        {/* Document Share Dialog */}
        <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Share Document</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-96">
              <div className="space-y-2">
                {userDocuments.map(doc => (
                  <Card key={doc.udt_id} className="cursor-pointer hover:bg-gray-50">
                    <CardContent className="p-4" onClick={() => !sharingDocId && shareDocument(doc)}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">{doc.file_name}</p>
                          <p className="text-sm text-gray-600">UDT ID: {doc.udt_id}</p>
                        </div>
                        <Button
                          size="sm"
                          disabled={!!sharingDocId}
                          style={{ background: sharingDocId === doc.udt_id ? '#9ca3af' : '#0F52BA' }}
                          className="flex items-center justify-center gap-2"
                        >
                          {sharingDocId === doc.udt_id ? (
                            <>
                              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Sharing…</span>
                            </>
                          ) : (
                            <span>Share</span>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Reshare (Forward) Dialog - when user has allow_reshare on a document in chat */}
        <Dialog open={showReshareDialog} onOpenChange={(open) => { setShowReshareDialog(open); if (!open) setReshareDoc(null); }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Forward className="w-5 h-5" style={{ color: '#0F52BA' }} />
                Forward / Reshare Document
              </DialogTitle>
            </DialogHeader>
            {reshareDoc && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg border bg-gray-50">
                  <p className="font-semibold break-all">{reshareDoc.file_name || 'Document'}</p>
                  <p className="text-xs text-gray-600 font-mono">UDT: {reshareDoc.udt_id}</p>
                </div>
                <div className="space-y-2">
                  <Label>Recipient (search by name, email, or UDT User ID)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={reshareSearchQuery}
                      onChange={(e) => { setReshareSearchQuery(e.target.value); setReshareRecipient(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchUsersForReshare(reshareSearchQuery))}
                      placeholder="Search recipient…"
                    />
                    <Button variant="outline" onClick={() => searchUsersForReshare(reshareSearchQuery)} title="Search">
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                  {reshareSearchResults.length > 0 && !reshareRecipient && (
                    <div className="border rounded max-h-48 overflow-y-auto divide-y bg-white">
                      {reshareSearchResults.map((u) => (
                        <button
                          type="button"
                          key={u.user_id}
                          onClick={() => { setReshareRecipient(u); setReshareSearchResults([]); setReshareSearchQuery(''); }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50"
                        >
                          <p className="font-semibold">{u.name}</p>
                          <p className="text-xs text-gray-600 break-all">{u.email}</p>
                          {u.udt_user_id && <Badge variant="outline" className="text-xs mt-1">{u.udt_user_id}</Badge>}
                        </button>
                      ))}
                    </div>
                  )}
                  {reshareRecipient && (
                    <div className="p-3 rounded border bg-gray-50 flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{reshareRecipient.name}</p>
                        <p className="text-xs text-gray-600 break-all">{reshareRecipient.email}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setReshareRecipient(null)}>Change</Button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Message (optional)</Label>
                  <Input
                    value={reshareMessage}
                    onChange={(e) => setReshareMessage(e.target.value)}
                    placeholder="Add a message…"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Expiry (hours)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={168}
                      value={reshareExpiryHours}
                      onChange={(e) => setReshareExpiryHours(parseInt(e.target.value, 10) || 24)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Permissions</Label>
                    <select
                      value={resharePermissions}
                      onChange={(e) => setResharePermissions(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="view_only">View Only</option>
                      <option value="download_view">Download and View</option>
                      <option value="print_download_view">Print, Download and View</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Reshare permission cannot be granted onward (owner-only).</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowReshareDialog(false)}>Cancel</Button>
                  <Button
                    onClick={submitReshare}
                    disabled={!reshareRecipient || reshareLoading}
                    style={{ background: '#0F52BA' }}
                  >
                    {reshareLoading ? 'Sending…' : (
                      <>
                        <Forward className="w-4 h-4 mr-2" />
                        Forward
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Share History Panel */}
        <Dialog open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
          <DialogContent className="max-w-6xl w-full">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Share History
                  </p>
                  <p className="text-sm font-normal text-gray-500">
                    Review everything shared with your contacts across time.
                  </p>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span>Total records: {filteredShareHistory.length}</span>
                  <Button variant="outline" size="sm" onClick={fetchShareHistory}>
                    Refresh
                  </Button>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="grid lg:grid-cols-[320px,1fr] gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Filters</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHistoryFiltersPanel((prev) => !prev)}
                  >
                    {showHistoryFiltersPanel ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {showHistoryFiltersPanel ? (
                  <div className="rounded-lg border bg-white p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    {renderHistoryFilters()}
                    {historyActiveFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={clearHistoryFilters}
                      >
                        Clear all filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-blue-600 bg-blue-50 p-3 rounded">
                    <strong>{historyActiveFilterCount}</strong> active filter
                    {historyActiveFilterCount === 1 ? '' : 's'} – click "Show" to adjust.
                  </div>
                )}
              </div>

              <div className="rounded-lg border bg-gray-50 p-4">
                <ScrollArea className="max-h-[60vh]">
                  {historyLoading ? (
                    <div className="py-12 text-center text-gray-500 space-y-2">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto" />
                      <p className="text-sm">Loading history...</p>
                    </div>
                  ) : filteredShareHistory.length === 0 ? (
                    <div className="py-12 text-center text-gray-500 space-y-2">
                      <Activity className="w-8 h-8 mx-auto" />
                      <p>No history matches the selected filters.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredShareHistory.map((share) => (
                        <Card key={share.share_id}>
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-semibold">{share.recipient?.name || 'Unknown recipient'}</p>
                                <p className="text-xs text-gray-500">{share.recipient?.email || 'No email provided'}</p>
                              </div>
                              <Badge variant="secondary" className="capitalize">
                                {share.status || 'unknown'}
                              </Badge>
                            </div>
                            <div className="text-sm space-y-1">
                              <p className="font-medium">{share.document?.file_name || share.udt_id}</p>
                              <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                                <span>Document UDT: {share.udt_id}</span>
                                {share.recipient?.udt_user_id && (
                                  <button
                                    onClick={() => handleUDTIdClick(share.recipient.udt_user_id)}
                                    className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                                    title="View timeline for this recipient"
                                  >
                                    Timeline: {share.recipient.udt_user_id}
                                    <ExternalLink className="w-3 h-3" />
                                  </button>
                                )}
                                {share.document?.file_type && (
                                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                                    {share.document.file_type}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-600">
                                Purpose: {share.document?.purpose || share.message || '—'}
                              </p>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Shared {formatShareDate(share.created_at)}</span>
                              <span>Expires {formatShareDate(share.expiry)}</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Timeline Dialog */}
        <Dialog open={showTimeline} onOpenChange={setShowTimeline}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Activity Timeline
                {timelineData?.target_user && (
                  <span className="text-sm font-normal text-gray-500">
                    with {timelineData.target_user.name} ({timelineData.target_user.udt_user_id})
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              {timelineLoading ? (
                <div className="py-12 text-center text-gray-500 space-y-2">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto" />
                  <p className="text-sm">Loading timeline...</p>
                </div>
              ) : combinedTimeline.length === 0 ? (
                <div className="py-12 text-center text-gray-500 space-y-2">
                  <Activity className="w-8 h-8 mx-auto" />
                  <p>No activity history found.</p>
                </div>
              ) : (
                <div className="relative py-2">
                  {/* Vertical rail */}
                  <div className="absolute left-[108px] top-0 bottom-0 w-px bg-gradient-to-b from-gray-300 via-gray-200 to-transparent" />

                  <div className="space-y-6">
                    {combinedTimeline.map((event, index) => {
                      const ts = new Date(event.timestamp);
                      const title = getTimelineEventTitle(event);
                      const status = getTimelineEventStatus(event);
                      const dateLabel = ts.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
                      const timeLabel = ts.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

                      const showText =
                        (event.type === 'share_bundle' && (event.chat_text || event.share_message)) ||
                        (event.type === 'message' && event.content?.text) ||
                        (event.type === 'share' && event.message);

                      const textValue =
                        (event.type === 'share_bundle' ? (event.chat_text || event.share_message) : null) ||
                        (event.type === 'message' ? event.content?.text : null) ||
                        (event.type === 'share' ? event.message : null) ||
                        '';

                      return (
                        <div key={`${event.type}-${event.timestamp}-${index}`} className="flex items-start gap-4">
                          {/* Left timestamp column */}
                          <div className="w-24 text-right leading-tight">
                            <div className="text-sm font-semibold text-gray-800">{dateLabel}</div>
                            <div className="text-xs text-gray-500">{timeLabel}</div>
                          </div>

                          {/* Dot */}
                          <div className="relative w-6 flex justify-center">
                            <div className={`mt-1 h-3 w-3 rounded-full ${status.dotClass} ring-4 ring-white shadow`} />
                          </div>

                          {/* Card (format like screenshot) */}
                          <div className="flex-1 min-w-0">
                            <div className="rounded-xl bg-gray-900 text-white shadow-sm border border-gray-800">
                              <div className="px-4 py-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="font-semibold truncate">{title}</div>
                                    <div className="mt-1 flex items-center gap-2 text-sm text-gray-200">
                                      <span className={`inline-block h-2 w-2 rounded-full ${status.dotClass}`} />
                                      <span className="truncate">{status.label}</span>
                                    </div>
                                  </div>

                                  {/* Small meta */}
                                  <div className="text-xs text-gray-300 text-right whitespace-nowrap">
                                    {event.document_type ? <div className="uppercase">{event.document_type}</div> : null}
                                    {event.status ? <div className="capitalize">{event.status}</div> : null}
                                  </div>
                                </div>

                                {event.udt_id && (
                                  <div className="mt-2 text-xs text-gray-300 font-mono truncate">
                                    UDT: {event.udt_id}
                                  </div>
                                )}

                                {event.purpose && (
                                  <div className="mt-2 text-xs text-gray-200 truncate">
                                    Purpose: {event.purpose}
                                  </div>
                                )}

                                {showText ? (
                                  <div className="mt-2 text-sm text-gray-100/90 italic line-clamp-2">
                                    “{textValue}”
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </ScrollArea>
            {timelineData && (
              <div className="mt-4 pt-4 border-t text-sm text-gray-500">
                Total events: {combinedTimeline.length}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Document History Dialog - where this document came from (Owner → 2nd → … → You) */}
        <Dialog open={documentHistoryOpen} onOpenChange={(open) => { setDocumentHistoryOpen(open); if (!open) setDocumentHistoryData(null); }}>
          <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" style={{ color: '#0F52BA' }} />
                Document history
              </DialogTitle>
            </DialogHeader>
            {documentHistoryLoading ? (
              <div className="py-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto" />
                <p className="text-sm mt-2">Loading...</p>
              </div>
            ) : documentHistoryData ? (
              <div className="space-y-4 overflow-y-auto pr-1">
                <p className="text-sm text-gray-600">
                  Where this document came from
                </p>
                <div className="font-semibold text-gray-900 truncate" title={documentHistoryData.document_name}>
                  {documentHistoryData.document_name}
                </div>
                {documentHistoryData.udt_id && (
                  <p className="text-xs text-gray-500 font-mono">UDT: {documentHistoryData.udt_id}</p>
                )}
                <ol className="space-y-3 list-decimal list-inside">
                  {documentHistoryData.chain && documentHistoryData.chain.length > 0 ? (
                    documentHistoryData.chain.map((hop, index) => {
                      const isOwnerStep = index === 0;
                      const receiverName = hop.receiver_name || hop.requester_name || hop.recipient_name || hop.receiver || 'Unknown';
                      const isYou = hop.receiver === user?.user_id;
                      const label = isOwnerStep
                        ? `Owner: ${hop.sender_name || 'Document Owner'}`
                        : `Shared to ${isYou ? 'You' : receiverName}${hop.recipient_status === 'revoked' ? ' (revoked)' : ''}`;
                      return (
                        <li
                          key={hop.hop_id || index}
                          className="text-sm py-2 px-3 rounded-lg border bg-gray-50"
                          style={{ borderColor: '#E8F2FF' }}
                        >
                          <span className="font-medium text-gray-900">{label}</span>
                          {hop.timestamp && (
                            <span className="block text-xs text-gray-500 mt-0.5">
                              {new Date(hop.timestamp).toLocaleString()}
                            </span>
                          )}
                        </li>
                      );
                    })
                  ) : (
                    <li className="text-sm text-gray-500 py-2">No history available.</li>
                  )}
                </ol>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Clear Chat Dialog */}
        <AlertDialog open={clearChatDialogOpen} onOpenChange={setClearChatDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear chat?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove all messages from your chat screen. The other person will still see their chat history.
              </AlertDialogDescription>
            </AlertDialogHeader>
              <AlertDialogFooter>
              <AlertDialogCancel disabled={clearingChat}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2"
                onClick={clearChat}
                disabled={clearingChat}
              >
                {clearingChat ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Clearing…</span>
                  </>
                ) : (
                  <span>Clear</span>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
